/** Власне меню модуля з метаданих (прототип v4: «Аналітичні інструменти», «Конструктор законопроєктів»). Чисті функції. */
import { localized } from '@platform/core/lib/localized';
import type { IMenuItem, IModuleDefinition, IModuleMenuI18n, IModuleStrings, IRecordTypeDefinition } from '@platform/core/types';
import { whereItemKey } from './adHocEntry';
import { buildMenu } from './buildMenu';
import type { IMenuEntry } from './IMenuEntry';
import type { IModuleMenuGroup } from './IModuleMenuGroup';
import { isHiddenType, mainRecordTypeOf, tableEntryKeyOf } from './moduleSections';

/**
 * Ключ `?item=` пункту власного меню в меню його типу: пункт із тим самим фільтром (прототип: «Діаграми fishbone» відкриває той самий
 * список, що й пункт «Fishbone» меню аналізів, — із чипом «Fishbone ×»), пресет — `preset:<ключ>`, таблиця — «Усі …» типу (tableEntryKeyOf);
 * фільтр, якого меню типу не має, — пункт поза меню `where:<фільтр>`.
 */
export function entryKeyOf(rt: IRecordTypeDefinition, item: IMenuItem, strings: IModuleStrings, moduleName: string): string {
  if (item.preset) return `preset:${item.preset}`;
  if (!item.filter) return tableEntryKeyOf(rt);
  const own = buildMenu(rt, {}, strings, moduleName).flatMap((s) => s.entries).find((e) => e.filter === item.filter && !e.preset);
  return own?.key ?? whereItemKey(item.filter);
}

/**
 * Групи власного меню модуля (`definition.menu`) мовою інтерфейсу: підписи — рядки пакета модуля `menu.groups` / `menu.items` (`labels`) за
 * англійським підписом, інакше підпис визначення; пункт без `recordType` — головного типу; пункт типу, якого визначення не має (не встановлено
 * в просторі), пропускається, як і група без пунктів (прототип: ts.filter(… wsx.modules.some …)). Без власного меню — порожньо.
 */
export function moduleMenuOf(
  definition: IModuleDefinition, strings: IModuleStrings, labels: IModuleMenuI18n | undefined, moduleName: string,
): IModuleMenuGroup[] {
  const main = mainRecordTypeOf(definition);
  if (!main) return [];
  return (definition.menu ?? []).map((g, gi): IModuleMenuGroup => {
    const title = labels?.groups?.[g.group.en ?? ''] ?? localized(g.group, strings.lang);
    const items = (g.items ?? []).flatMap((it, i) => {
      const rt = definition.recordTypes.find((r) => r.key === (it.recordType ?? main.key));
      if (!rt || isHiddenType(rt)) return [];
      const entry: IMenuEntry = {
        key: entryKeyOf(rt, it, strings, moduleName), label: labels?.items?.[it.label.en ?? ''] ?? localized(it.label, strings.lang), group: title,
        view: 'table', ...(it.filter ? { filter: it.filter } : {}), ...(it.preset ? { preset: it.preset } : {}),
      };
      return [{ key: `${gi}.${i}`, rtKey: rt.key, entry }];
    });
    return { id: `module:${g.group.en || gi}`, title, collapsed: Boolean(g.collapsed), items };
  }).filter((g) => g.items.length > 0);
}

/**
 * Активний пункт власного меню (прототип: лише на сторінці списку його типу, у поданні «Таблиця» — `view` маршруту або типове подання типу
 * `defaultView`, — з тим самим пунктом `item`, без нього — «Усі …» типу); на сторінках модуля й запису — жодного.
 */
export function moduleMenuActiveKey(
  groups: IModuleMenuGroup[], route: { rt?: string; id?: string; view?: string }, item: string | null, defaultView: string | undefined,
  tableKey: string | undefined,
): string | null {
  if (!route.rt || route.id || (route.view ?? defaultView) !== 'table') return null;
  const key = item ?? tableKey;
  return groups.flatMap((g) => g.items).find((i) => i.rtKey === route.rt && i.entry.key === key)?.key ?? null;
}

/** Група розгорнута типово: не згорнута в метаданих, має активний пункт або пункт усієї таблиці поточного типу `current` (прототип grpA). */
export function moduleGroupOpen(group: IModuleMenuGroup, active: string | null, current: string | undefined): boolean {
  return !group.collapsed || group.items.some((i) => i.key === active || (i.rtKey === current && !i.entry.filter && !i.entry.preset));
}
